/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

// Code generated by the Google Gen AI SDK generator DO NOT EDIT.

import {ApiClient} from '../_api_client.js';
import * as common from '../_common.js';
import * as t from '../_transformers.js';
import type * as types from '../types.js';

export function authConfigToMldev(
  fromObject: types.AuthConfig,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromApiKey = common.getValueByPath(fromObject, ['apiKey']);
  if (fromApiKey != null) {
    common.setValueByPath(toObject, ['apiKey'], fromApiKey);
  }

  if (common.getValueByPath(fromObject, ['apiKeyConfig']) !== undefined) {
    throw new Error(
      'apiKeyConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['authType']) !== undefined) {
    throw new Error(
      'authType parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['googleServiceAccountConfig']) !==
    undefined
  ) {
    throw new Error(
      'googleServiceAccountConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['httpBasicAuthConfig']) !== undefined
  ) {
    throw new Error(
      'httpBasicAuthConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['oauthConfig']) !== undefined) {
    throw new Error(
      'oauthConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['oidcConfig']) !== undefined) {
    throw new Error(
      'oidcConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function batchJobDestinationFromMldev(
  fromObject: types.BatchJobDestination,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFileName = common.getValueByPath(fromObject, ['responsesFile']);
  if (fromFileName != null) {
    common.setValueByPath(toObject, ['fileName'], fromFileName);
  }

  const fromInlinedResponses = common.getValueByPath(fromObject, [
    'inlinedResponses',
    'inlinedResponses',
  ]);
  if (fromInlinedResponses != null) {
    let transformedList = fromInlinedResponses;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return inlinedResponseFromMldev(item);
      });
    }
    common.setValueByPath(toObject, ['inlinedResponses'], transformedList);
  }

  const fromInlinedEmbedContentResponses = common.getValueByPath(fromObject, [
    'inlinedEmbedContentResponses',
    'inlinedResponses',
  ]);
  if (fromInlinedEmbedContentResponses != null) {
    let transformedList = fromInlinedEmbedContentResponses;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(
      toObject,
      ['inlinedEmbedContentResponses'],
      transformedList,
    );
  }

  return toObject;
}

export function batchJobDestinationFromVertex(
  fromObject: types.BatchJobDestination,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFormat = common.getValueByPath(fromObject, ['predictionsFormat']);
  if (fromFormat != null) {
    common.setValueByPath(toObject, ['format'], fromFormat);
  }

  const fromGcsUri = common.getValueByPath(fromObject, [
    'gcsDestination',
    'outputUriPrefix',
  ]);
  if (fromGcsUri != null) {
    common.setValueByPath(toObject, ['gcsUri'], fromGcsUri);
  }

  const fromBigqueryUri = common.getValueByPath(fromObject, [
    'bigqueryDestination',
    'outputUri',
  ]);
  if (fromBigqueryUri != null) {
    common.setValueByPath(toObject, ['bigqueryUri'], fromBigqueryUri);
  }

  const fromVertexDataset = common.getValueByPath(fromObject, [
    'vertexMultimodalDatasetDestination',
  ]);
  if (fromVertexDataset != null) {
    common.setValueByPath(
      toObject,
      ['vertexDataset'],
      vertexMultimodalDatasetDestinationFromVertex(fromVertexDataset),
    );
  }

  return toObject;
}

export function batchJobDestinationToVertex(
  fromObject: types.BatchJobDestination,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFormat = common.getValueByPath(fromObject, ['format']);
  if (fromFormat != null) {
    common.setValueByPath(toObject, ['predictionsFormat'], fromFormat);
  }

  const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
  if (fromGcsUri != null) {
    common.setValueByPath(
      toObject,
      ['gcsDestination', 'outputUriPrefix'],
      fromGcsUri,
    );
  }

  const fromBigqueryUri = common.getValueByPath(fromObject, ['bigqueryUri']);
  if (fromBigqueryUri != null) {
    common.setValueByPath(
      toObject,
      ['bigqueryDestination', 'outputUri'],
      fromBigqueryUri,
    );
  }

  const fromVertexDataset = common.getValueByPath(fromObject, [
    'vertexDataset',
  ]);
  if (fromVertexDataset != null) {
    common.setValueByPath(
      toObject,
      ['vertexMultimodalDatasetDestination'],
      vertexMultimodalDatasetDestinationToVertex(fromVertexDataset),
    );
  }

  if (common.getValueByPath(fromObject, ['fileName']) !== undefined) {
    throw new Error(
      'fileName parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['inlinedResponses']) !== undefined) {
    throw new Error(
      'inlinedResponses parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['inlinedEmbedContentResponses']) !==
    undefined
  ) {
    throw new Error(
      'inlinedEmbedContentResponses parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function batchJobFromMldev(
  fromObject: types.BatchJob,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromDisplayName = common.getValueByPath(fromObject, [
    'metadata',
    'displayName',
  ]);
  if (fromDisplayName != null) {
    common.setValueByPath(toObject, ['displayName'], fromDisplayName);
  }

  const fromState = common.getValueByPath(fromObject, ['metadata', 'state']);
  if (fromState != null) {
    common.setValueByPath(toObject, ['state'], t.tJobState(fromState));
  }

  const fromCreateTime = common.getValueByPath(fromObject, [
    'metadata',
    'createTime',
  ]);
  if (fromCreateTime != null) {
    common.setValueByPath(toObject, ['createTime'], fromCreateTime);
  }

  const fromEndTime = common.getValueByPath(fromObject, [
    'metadata',
    'endTime',
  ]);
  if (fromEndTime != null) {
    common.setValueByPath(toObject, ['endTime'], fromEndTime);
  }

  const fromUpdateTime = common.getValueByPath(fromObject, [
    'metadata',
    'updateTime',
  ]);
  if (fromUpdateTime != null) {
    common.setValueByPath(toObject, ['updateTime'], fromUpdateTime);
  }

  const fromModel = common.getValueByPath(fromObject, ['metadata', 'model']);
  if (fromModel != null) {
    common.setValueByPath(toObject, ['model'], fromModel);
  }

  const fromDest = common.getValueByPath(fromObject, ['metadata', 'output']);
  if (fromDest != null) {
    common.setValueByPath(
      toObject,
      ['dest'],
      batchJobDestinationFromMldev(t.tRecvBatchJobDestination(fromDest)),
    );
  }

  return toObject;
}

export function batchJobFromVertex(
  fromObject: types.BatchJob,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (fromDisplayName != null) {
    common.setValueByPath(toObject, ['displayName'], fromDisplayName);
  }

  const fromState = common.getValueByPath(fromObject, ['state']);
  if (fromState != null) {
    common.setValueByPath(toObject, ['state'], t.tJobState(fromState));
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  const fromCreateTime = common.getValueByPath(fromObject, ['createTime']);
  if (fromCreateTime != null) {
    common.setValueByPath(toObject, ['createTime'], fromCreateTime);
  }

  const fromStartTime = common.getValueByPath(fromObject, ['startTime']);
  if (fromStartTime != null) {
    common.setValueByPath(toObject, ['startTime'], fromStartTime);
  }

  const fromEndTime = common.getValueByPath(fromObject, ['endTime']);
  if (fromEndTime != null) {
    common.setValueByPath(toObject, ['endTime'], fromEndTime);
  }

  const fromUpdateTime = common.getValueByPath(fromObject, ['updateTime']);
  if (fromUpdateTime != null) {
    common.setValueByPath(toObject, ['updateTime'], fromUpdateTime);
  }

  const fromModel = common.getValueByPath(fromObject, ['model']);
  if (fromModel != null) {
    common.setValueByPath(toObject, ['model'], fromModel);
  }

  const fromSrc = common.getValueByPath(fromObject, ['inputConfig']);
  if (fromSrc != null) {
    common.setValueByPath(toObject, ['src'], batchJobSourceFromVertex(fromSrc));
  }

  const fromDest = common.getValueByPath(fromObject, ['outputConfig']);
  if (fromDest != null) {
    common.setValueByPath(
      toObject,
      ['dest'],
      batchJobDestinationFromVertex(t.tRecvBatchJobDestination(fromDest)),
    );
  }

  const fromOutputInfo = common.getValueByPath(fromObject, ['outputInfo']);
  if (fromOutputInfo != null) {
    common.setValueByPath(toObject, ['outputInfo'], fromOutputInfo);
  }

  const fromCompletionStats = common.getValueByPath(fromObject, [
    'completionStats',
  ]);
  if (fromCompletionStats != null) {
    common.setValueByPath(toObject, ['completionStats'], fromCompletionStats);
  }

  return toObject;
}

export function batchJobSourceFromVertex(
  fromObject: types.BatchJobSource,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFormat = common.getValueByPath(fromObject, ['instancesFormat']);
  if (fromFormat != null) {
    common.setValueByPath(toObject, ['format'], fromFormat);
  }

  const fromGcsUri = common.getValueByPath(fromObject, ['gcsSource', 'uris']);
  if (fromGcsUri != null) {
    common.setValueByPath(toObject, ['gcsUri'], fromGcsUri);
  }

  const fromBigqueryUri = common.getValueByPath(fromObject, [
    'bigquerySource',
    'inputUri',
  ]);
  if (fromBigqueryUri != null) {
    common.setValueByPath(toObject, ['bigqueryUri'], fromBigqueryUri);
  }

  const fromVertexDatasetName = common.getValueByPath(fromObject, [
    'vertexMultimodalDatasetSource',
    'datasetName',
  ]);
  if (fromVertexDatasetName != null) {
    common.setValueByPath(
      toObject,
      ['vertexDatasetName'],
      fromVertexDatasetName,
    );
  }

  return toObject;
}

export function batchJobSourceToMldev(
  apiClient: ApiClient,
  fromObject: types.BatchJobSource,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['format']) !== undefined) {
    throw new Error(
      'format parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['gcsUri']) !== undefined) {
    throw new Error(
      'gcsUri parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['bigqueryUri']) !== undefined) {
    throw new Error(
      'bigqueryUri parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['vertexDatasetName']) !== undefined) {
    throw new Error(
      'vertexDatasetName parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromFileName = common.getValueByPath(fromObject, ['fileName']);
  if (fromFileName != null) {
    common.setValueByPath(toObject, ['fileName'], fromFileName);
  }

  const fromInlinedRequests = common.getValueByPath(fromObject, [
    'inlinedRequests',
  ]);
  if (fromInlinedRequests != null) {
    let transformedList = fromInlinedRequests;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return inlinedRequestToMldev(apiClient, item);
      });
    }
    common.setValueByPath(toObject, ['requests', 'requests'], transformedList);
  }

  return toObject;
}

export function batchJobSourceToVertex(
  fromObject: types.BatchJobSource,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFormat = common.getValueByPath(fromObject, ['format']);
  if (fromFormat != null) {
    common.setValueByPath(toObject, ['instancesFormat'], fromFormat);
  }

  const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
  if (fromGcsUri != null) {
    common.setValueByPath(toObject, ['gcsSource', 'uris'], fromGcsUri);
  }

  const fromBigqueryUri = common.getValueByPath(fromObject, ['bigqueryUri']);
  if (fromBigqueryUri != null) {
    common.setValueByPath(
      toObject,
      ['bigquerySource', 'inputUri'],
      fromBigqueryUri,
    );
  }

  const fromVertexDatasetName = common.getValueByPath(fromObject, [
    'vertexDatasetName',
  ]);
  if (fromVertexDatasetName != null) {
    common.setValueByPath(
      toObject,
      ['vertexMultimodalDatasetSource', 'datasetName'],
      fromVertexDatasetName,
    );
  }

  if (common.getValueByPath(fromObject, ['fileName']) !== undefined) {
    throw new Error(
      'fileName parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['inlinedRequests']) !== undefined) {
    throw new Error(
      'inlinedRequests parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function blobToMldev(fromObject: types.Blob): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromData = common.getValueByPath(fromObject, ['data']);
  if (fromData != null) {
    common.setValueByPath(toObject, ['data'], fromData);
  }

  if (common.getValueByPath(fromObject, ['displayName']) !== undefined) {
    throw new Error(
      'displayName parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromMimeType = common.getValueByPath(fromObject, ['mimeType']);
  if (fromMimeType != null) {
    common.setValueByPath(toObject, ['mimeType'], fromMimeType);
  }

  return toObject;
}

export function cancelBatchJobParametersToMldev(
  apiClient: ApiClient,
  fromObject: types.CancelBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function cancelBatchJobParametersToVertex(
  apiClient: ApiClient,
  fromObject: types.CancelBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function candidateFromMldev(
  fromObject: types.Candidate,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromContent = common.getValueByPath(fromObject, ['content']);
  if (fromContent != null) {
    common.setValueByPath(toObject, ['content'], fromContent);
  }

  const fromCitationMetadata = common.getValueByPath(fromObject, [
    'citationMetadata',
  ]);
  if (fromCitationMetadata != null) {
    common.setValueByPath(
      toObject,
      ['citationMetadata'],
      citationMetadataFromMldev(fromCitationMetadata),
    );
  }

  const fromTokenCount = common.getValueByPath(fromObject, ['tokenCount']);
  if (fromTokenCount != null) {
    common.setValueByPath(toObject, ['tokenCount'], fromTokenCount);
  }

  const fromFinishReason = common.getValueByPath(fromObject, ['finishReason']);
  if (fromFinishReason != null) {
    common.setValueByPath(toObject, ['finishReason'], fromFinishReason);
  }

  const fromGroundingMetadata = common.getValueByPath(fromObject, [
    'groundingMetadata',
  ]);
  if (fromGroundingMetadata != null) {
    common.setValueByPath(
      toObject,
      ['groundingMetadata'],
      fromGroundingMetadata,
    );
  }

  const fromAvgLogprobs = common.getValueByPath(fromObject, ['avgLogprobs']);
  if (fromAvgLogprobs != null) {
    common.setValueByPath(toObject, ['avgLogprobs'], fromAvgLogprobs);
  }

  const fromIndex = common.getValueByPath(fromObject, ['index']);
  if (fromIndex != null) {
    common.setValueByPath(toObject, ['index'], fromIndex);
  }

  const fromLogprobsResult = common.getValueByPath(fromObject, [
    'logprobsResult',
  ]);
  if (fromLogprobsResult != null) {
    common.setValueByPath(toObject, ['logprobsResult'], fromLogprobsResult);
  }

  const fromSafetyRatings = common.getValueByPath(fromObject, [
    'safetyRatings',
  ]);
  if (fromSafetyRatings != null) {
    let transformedList = fromSafetyRatings;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['safetyRatings'], transformedList);
  }

  const fromUrlContextMetadata = common.getValueByPath(fromObject, [
    'urlContextMetadata',
  ]);
  if (fromUrlContextMetadata != null) {
    common.setValueByPath(
      toObject,
      ['urlContextMetadata'],
      fromUrlContextMetadata,
    );
  }

  return toObject;
}

export function citationMetadataFromMldev(
  fromObject: types.CitationMetadata,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromCitations = common.getValueByPath(fromObject, ['citationSources']);
  if (fromCitations != null) {
    let transformedList = fromCitations;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['citations'], transformedList);
  }

  return toObject;
}

export function contentToMldev(
  fromObject: types.Content,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromParts = common.getValueByPath(fromObject, ['parts']);
  if (fromParts != null) {
    let transformedList = fromParts;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return partToMldev(item);
      });
    }
    common.setValueByPath(toObject, ['parts'], transformedList);
  }

  const fromRole = common.getValueByPath(fromObject, ['role']);
  if (fromRole != null) {
    common.setValueByPath(toObject, ['role'], fromRole);
  }

  return toObject;
}

export function createBatchJobConfigToMldev(
  fromObject: types.CreateBatchJobConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (parentObject !== undefined && fromDisplayName != null) {
    common.setValueByPath(
      parentObject,
      ['batch', 'displayName'],
      fromDisplayName,
    );
  }

  if (common.getValueByPath(fromObject, ['dest']) !== undefined) {
    throw new Error(
      'dest parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromWebhookConfig = common.getValueByPath(fromObject, [
    'webhookConfig',
  ]);
  if (parentObject !== undefined && fromWebhookConfig != null) {
    common.setValueByPath(
      parentObject,
      ['batch', 'webhookConfig'],
      fromWebhookConfig,
    );
  }

  return toObject;
}

export function createBatchJobConfigToVertex(
  fromObject: types.CreateBatchJobConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (parentObject !== undefined && fromDisplayName != null) {
    common.setValueByPath(parentObject, ['displayName'], fromDisplayName);
  }

  const fromDest = common.getValueByPath(fromObject, ['dest']);
  if (parentObject !== undefined && fromDest != null) {
    common.setValueByPath(
      parentObject,
      ['outputConfig'],
      batchJobDestinationToVertex(t.tBatchJobDestination(fromDest)),
    );
  }

  if (common.getValueByPath(fromObject, ['webhookConfig']) !== undefined) {
    throw new Error(
      'webhookConfig parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function createBatchJobParametersToMldev(
  apiClient: ApiClient,
  fromObject: types.CreateBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModel = common.getValueByPath(fromObject, ['model']);
  if (fromModel != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'model'],
      t.tModel(apiClient, fromModel),
    );
  }

  const fromSrc = common.getValueByPath(fromObject, ['src']);
  if (fromSrc != null) {
    common.setValueByPath(
      toObject,
      ['batch', 'inputConfig'],
      batchJobSourceToMldev(apiClient, t.tBatchJobSource(apiClient, fromSrc)),
    );
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createBatchJobConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function createBatchJobParametersToVertex(
  apiClient: ApiClient,
  fromObject: types.CreateBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModel = common.getValueByPath(fromObject, ['model']);
  if (fromModel != null) {
    common.setValueByPath(toObject, ['model'], t.tModel(apiClient, fromModel));
  }

  const fromSrc = common.getValueByPath(fromObject, ['src']);
  if (fromSrc != null) {
    common.setValueByPath(
      toObject,
      ['inputConfig'],
      batchJobSourceToVertex(t.tBatchJobSource(apiClient, fromSrc)),
    );
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createBatchJobConfigToVertex(fromConfig, toObject);
  }

  return toObject;
}

export function createEmbeddingsBatchJobConfigToMldev(
  fromObject: types.CreateEmbeddingsBatchJobConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (parentObject !== undefined && fromDisplayName != null) {
    common.setValueByPath(
      parentObject,
      ['batch', 'displayName'],
      fromDisplayName,
    );
  }

  return toObject;
}

export function createEmbeddingsBatchJobParametersToMldev(
  apiClient: ApiClient,
  fromObject: types.CreateEmbeddingsBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModel = common.getValueByPath(fromObject, ['model']);
  if (fromModel != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'model'],
      t.tModel(apiClient, fromModel),
    );
  }

  const fromSrc = common.getValueByPath(fromObject, ['src']);
  if (fromSrc != null) {
    common.setValueByPath(
      toObject,
      ['batch', 'inputConfig'],
      embeddingsBatchJobSourceToMldev(apiClient, fromSrc),
    );
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createEmbeddingsBatchJobConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function deleteBatchJobParametersToMldev(
  apiClient: ApiClient,
  fromObject: types.DeleteBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function deleteBatchJobParametersToVertex(
  apiClient: ApiClient,
  fromObject: types.DeleteBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function deleteResourceJobFromMldev(
  fromObject: types.DeleteResourceJob,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromDone = common.getValueByPath(fromObject, ['done']);
  if (fromDone != null) {
    common.setValueByPath(toObject, ['done'], fromDone);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  return toObject;
}

export function deleteResourceJobFromVertex(
  fromObject: types.DeleteResourceJob,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromDone = common.getValueByPath(fromObject, ['done']);
  if (fromDone != null) {
    common.setValueByPath(toObject, ['done'], fromDone);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  return toObject;
}

export function embedContentBatchToMldev(
  apiClient: ApiClient,
  fromObject: types.EmbedContentBatch,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromContents = common.getValueByPath(fromObject, ['contents']);
  if (fromContents != null) {
    let transformedList = t.tContentsForEmbed(apiClient, fromContents);
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(
      toObject,
      ['requests[]', 'request', 'content'],
      transformedList,
    );
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    common.setValueByPath(
      toObject,
      ['_self'],
      embedContentConfigToMldev(fromConfig, toObject),
    );
    common.moveValueByPath(toObject, {'requests[].*': 'requests[].request.*'});
  }

  return toObject;
}

export function embedContentConfigToMldev(
  fromObject: types.EmbedContentConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromTaskType = common.getValueByPath(fromObject, ['taskType']);
  if (parentObject !== undefined && fromTaskType != null) {
    common.setValueByPath(
      parentObject,
      ['requests[]', 'taskType'],
      fromTaskType,
    );
  }

  const fromTitle = common.getValueByPath(fromObject, ['title']);
  if (parentObject !== undefined && fromTitle != null) {
    common.setValueByPath(parentObject, ['requests[]', 'title'], fromTitle);
  }

  const fromOutputDimensionality = common.getValueByPath(fromObject, [
    'outputDimensionality',
  ]);
  if (parentObject !== undefined && fromOutputDimensionality != null) {
    common.setValueByPath(
      parentObject,
      ['requests[]', 'outputDimensionality'],
      fromOutputDimensionality,
    );
  }

  if (common.getValueByPath(fromObject, ['mimeType']) !== undefined) {
    throw new Error(
      'mimeType parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['autoTruncate']) !== undefined) {
    throw new Error(
      'autoTruncate parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['documentOcr']) !== undefined) {
    throw new Error(
      'documentOcr parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['audioTrackExtraction']) !== undefined
  ) {
    throw new Error(
      'audioTrackExtraction parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function embeddingsBatchJobSourceToMldev(
  apiClient: ApiClient,
  fromObject: types.EmbeddingsBatchJobSource,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFileName = common.getValueByPath(fromObject, ['fileName']);
  if (fromFileName != null) {
    common.setValueByPath(toObject, ['file_name'], fromFileName);
  }

  const fromInlinedRequests = common.getValueByPath(fromObject, [
    'inlinedRequests',
  ]);
  if (fromInlinedRequests != null) {
    common.setValueByPath(
      toObject,
      ['requests'],
      embedContentBatchToMldev(apiClient, fromInlinedRequests),
    );
  }

  return toObject;
}

export function fileDataToMldev(
  fromObject: types.FileData,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['displayName']) !== undefined) {
    throw new Error(
      'displayName parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromFileUri = common.getValueByPath(fromObject, ['fileUri']);
  if (fromFileUri != null) {
    common.setValueByPath(toObject, ['fileUri'], fromFileUri);
  }

  const fromMimeType = common.getValueByPath(fromObject, ['mimeType']);
  if (fromMimeType != null) {
    common.setValueByPath(toObject, ['mimeType'], fromMimeType);
  }

  return toObject;
}

export function functionCallToMldev(
  fromObject: types.FunctionCall,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromArgs = common.getValueByPath(fromObject, ['args']);
  if (fromArgs != null) {
    common.setValueByPath(toObject, ['args'], fromArgs);
  }

  const fromId = common.getValueByPath(fromObject, ['id']);
  if (fromId != null) {
    common.setValueByPath(toObject, ['id'], fromId);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  if (common.getValueByPath(fromObject, ['partialArgs']) !== undefined) {
    throw new Error(
      'partialArgs parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['willContinue']) !== undefined) {
    throw new Error(
      'willContinue parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function functionCallingConfigToMldev(
  fromObject: types.FunctionCallingConfig,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromAllowedFunctionNames = common.getValueByPath(fromObject, [
    'allowedFunctionNames',
  ]);
  if (fromAllowedFunctionNames != null) {
    common.setValueByPath(
      toObject,
      ['allowedFunctionNames'],
      fromAllowedFunctionNames,
    );
  }

  const fromMode = common.getValueByPath(fromObject, ['mode']);
  if (fromMode != null) {
    common.setValueByPath(toObject, ['mode'], fromMode);
  }

  if (
    common.getValueByPath(fromObject, ['streamFunctionCallArguments']) !==
    undefined
  ) {
    throw new Error(
      'streamFunctionCallArguments parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function generateContentConfigToMldev(
  apiClient: ApiClient,
  fromObject: types.GenerateContentConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromServiceTier = common.getValueByPath(fromObject, ['serviceTier']);
  if (parentObject !== undefined && fromServiceTier != null) {
    common.setValueByPath(parentObject, ['serviceTier'], fromServiceTier);
  }

  const fromSystemInstruction = common.getValueByPath(fromObject, [
    'systemInstruction',
  ]);
  if (parentObject !== undefined && fromSystemInstruction != null) {
    common.setValueByPath(
      parentObject,
      ['systemInstruction'],
      contentToMldev(t.tContent(fromSystemInstruction)),
    );
  }

  const fromTemperature = common.getValueByPath(fromObject, ['temperature']);
  if (fromTemperature != null) {
    common.setValueByPath(toObject, ['temperature'], fromTemperature);
  }

  const fromTopP = common.getValueByPath(fromObject, ['topP']);
  if (fromTopP != null) {
    common.setValueByPath(toObject, ['topP'], fromTopP);
  }

  const fromTopK = common.getValueByPath(fromObject, ['topK']);
  if (fromTopK != null) {
    common.setValueByPath(toObject, ['topK'], fromTopK);
  }

  const fromCandidateCount = common.getValueByPath(fromObject, [
    'candidateCount',
  ]);
  if (fromCandidateCount != null) {
    common.setValueByPath(toObject, ['candidateCount'], fromCandidateCount);
  }

  const fromMaxOutputTokens = common.getValueByPath(fromObject, [
    'maxOutputTokens',
  ]);
  if (fromMaxOutputTokens != null) {
    common.setValueByPath(toObject, ['maxOutputTokens'], fromMaxOutputTokens);
  }

  const fromStopSequences = common.getValueByPath(fromObject, [
    'stopSequences',
  ]);
  if (fromStopSequences != null) {
    common.setValueByPath(toObject, ['stopSequences'], fromStopSequences);
  }

  const fromResponseLogprobs = common.getValueByPath(fromObject, [
    'responseLogprobs',
  ]);
  if (fromResponseLogprobs != null) {
    common.setValueByPath(toObject, ['responseLogprobs'], fromResponseLogprobs);
  }

  const fromLogprobs = common.getValueByPath(fromObject, ['logprobs']);
  if (fromLogprobs != null) {
    common.setValueByPath(toObject, ['logprobs'], fromLogprobs);
  }

  const fromPresencePenalty = common.getValueByPath(fromObject, [
    'presencePenalty',
  ]);
  if (fromPresencePenalty != null) {
    common.setValueByPath(toObject, ['presencePenalty'], fromPresencePenalty);
  }

  const fromFrequencyPenalty = common.getValueByPath(fromObject, [
    'frequencyPenalty',
  ]);
  if (fromFrequencyPenalty != null) {
    common.setValueByPath(toObject, ['frequencyPenalty'], fromFrequencyPenalty);
  }

  const fromSeed = common.getValueByPath(fromObject, ['seed']);
  if (fromSeed != null) {
    common.setValueByPath(toObject, ['seed'], fromSeed);
  }

  const fromResponseMimeType = common.getValueByPath(fromObject, [
    'responseMimeType',
  ]);
  if (fromResponseMimeType != null) {
    common.setValueByPath(toObject, ['responseMimeType'], fromResponseMimeType);
  }

  const fromResponseSchema = common.getValueByPath(fromObject, [
    'responseSchema',
  ]);
  if (fromResponseSchema != null) {
    common.setValueByPath(
      toObject,
      ['responseSchema'],
      t.tSchema(fromResponseSchema),
    );
  }

  const fromResponseJsonSchema = common.getValueByPath(fromObject, [
    'responseJsonSchema',
  ]);
  if (fromResponseJsonSchema != null) {
    common.setValueByPath(
      toObject,
      ['responseJsonSchema'],
      t.tJsonSchema(fromResponseJsonSchema),
    );
  }

  if (common.getValueByPath(fromObject, ['routingConfig']) !== undefined) {
    throw new Error(
      'routingConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['modelSelectionConfig']) !== undefined
  ) {
    throw new Error(
      'modelSelectionConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromSafetySettings = common.getValueByPath(fromObject, [
    'safetySettings',
  ]);
  if (parentObject !== undefined && fromSafetySettings != null) {
    let transformedList = fromSafetySettings;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return safetySettingToMldev(item);
      });
    }
    common.setValueByPath(parentObject, ['safetySettings'], transformedList);
  }

  const fromTools = common.getValueByPath(fromObject, ['tools']);
  if (parentObject !== undefined && fromTools != null) {
    let transformedList = t.tTools(fromTools);
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return toolToMldev(t.tTool(item));
      });
    }
    common.setValueByPath(parentObject, ['tools'], transformedList);
  }

  const fromToolConfig = common.getValueByPath(fromObject, ['toolConfig']);
  if (parentObject !== undefined && fromToolConfig != null) {
    common.setValueByPath(
      parentObject,
      ['toolConfig'],
      toolConfigToMldev(fromToolConfig),
    );
  }

  if (common.getValueByPath(fromObject, ['labels']) !== undefined) {
    throw new Error(
      'labels parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromCachedContent = common.getValueByPath(fromObject, [
    'cachedContent',
  ]);
  if (parentObject !== undefined && fromCachedContent != null) {
    common.setValueByPath(
      parentObject,
      ['cachedContent'],
      t.tCachedContentName(apiClient, fromCachedContent),
    );
  }

  const fromResponseModalities = common.getValueByPath(fromObject, [
    'responseModalities',
  ]);
  if (fromResponseModalities != null) {
    common.setValueByPath(
      toObject,
      ['responseModalities'],
      fromResponseModalities,
    );
  }

  const fromMediaResolution = common.getValueByPath(fromObject, [
    'mediaResolution',
  ]);
  if (fromMediaResolution != null) {
    common.setValueByPath(toObject, ['mediaResolution'], fromMediaResolution);
  }

  const fromSpeechConfig = common.getValueByPath(fromObject, ['speechConfig']);
  if (fromSpeechConfig != null) {
    common.setValueByPath(
      toObject,
      ['speechConfig'],
      t.tSpeechConfig(fromSpeechConfig),
    );
  }

  if (common.getValueByPath(fromObject, ['audioTimestamp']) !== undefined) {
    throw new Error(
      'audioTimestamp parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromThinkingConfig = common.getValueByPath(fromObject, [
    'thinkingConfig',
  ]);
  if (fromThinkingConfig != null) {
    common.setValueByPath(toObject, ['thinkingConfig'], fromThinkingConfig);
  }

  const fromAudioTranscriptionConfig = common.getValueByPath(fromObject, [
    'audioTranscriptionConfig',
  ]);
  if (fromAudioTranscriptionConfig != null) {
    common.setValueByPath(
      toObject,
      ['audioTranscriptionConfig'],
      fromAudioTranscriptionConfig,
    );
  }

  const fromImageConfig = common.getValueByPath(fromObject, ['imageConfig']);
  if (fromImageConfig != null) {
    common.setValueByPath(
      toObject,
      ['imageConfig'],
      imageConfigToMldev(fromImageConfig),
    );
  }

  const fromEnableEnhancedCivicAnswers = common.getValueByPath(fromObject, [
    'enableEnhancedCivicAnswers',
  ]);
  if (fromEnableEnhancedCivicAnswers != null) {
    common.setValueByPath(
      toObject,
      ['enableEnhancedCivicAnswers'],
      fromEnableEnhancedCivicAnswers,
    );
  }

  if (common.getValueByPath(fromObject, ['modelArmorConfig']) !== undefined) {
    throw new Error(
      'modelArmorConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function generateContentResponseFromMldev(
  fromObject: types.GenerateContentResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromCandidates = common.getValueByPath(fromObject, ['candidates']);
  if (fromCandidates != null) {
    let transformedList = fromCandidates;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return candidateFromMldev(item);
      });
    }
    common.setValueByPath(toObject, ['candidates'], transformedList);
  }

  const fromModelVersion = common.getValueByPath(fromObject, ['modelVersion']);
  if (fromModelVersion != null) {
    common.setValueByPath(toObject, ['modelVersion'], fromModelVersion);
  }

  const fromPromptFeedback = common.getValueByPath(fromObject, [
    'promptFeedback',
  ]);
  if (fromPromptFeedback != null) {
    common.setValueByPath(toObject, ['promptFeedback'], fromPromptFeedback);
  }

  const fromResponseId = common.getValueByPath(fromObject, ['responseId']);
  if (fromResponseId != null) {
    common.setValueByPath(toObject, ['responseId'], fromResponseId);
  }

  const fromUsageMetadata = common.getValueByPath(fromObject, [
    'usageMetadata',
  ]);
  if (fromUsageMetadata != null) {
    common.setValueByPath(toObject, ['usageMetadata'], fromUsageMetadata);
  }

  const fromModelStatus = common.getValueByPath(fromObject, ['modelStatus']);
  if (fromModelStatus != null) {
    common.setValueByPath(toObject, ['modelStatus'], fromModelStatus);
  }

  return toObject;
}

export function getBatchJobParametersToMldev(
  apiClient: ApiClient,
  fromObject: types.GetBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function getBatchJobParametersToVertex(
  apiClient: ApiClient,
  fromObject: types.GetBatchJobParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'name'],
      t.tBatchJobName(apiClient, fromName),
    );
  }

  return toObject;
}

export function googleMapsToMldev(
  fromObject: types.GoogleMaps,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromAuthConfig = common.getValueByPath(fromObject, ['authConfig']);
  if (fromAuthConfig != null) {
    common.setValueByPath(
      toObject,
      ['authConfig'],
      authConfigToMldev(fromAuthConfig),
    );
  }

  const fromEnableWidget = common.getValueByPath(fromObject, ['enableWidget']);
  if (fromEnableWidget != null) {
    common.setValueByPath(toObject, ['enableWidget'], fromEnableWidget);
  }

  if (common.getValueByPath(fromObject, ['groundingTypes']) !== undefined) {
    throw new Error(
      'groundingTypes parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function googleSearchToMldev(
  fromObject: types.GoogleSearch,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['blockingConfidence']) !== undefined) {
    throw new Error(
      'blockingConfidence parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['excludeDomains']) !== undefined) {
    throw new Error(
      'excludeDomains parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromSearchTypes = common.getValueByPath(fromObject, ['searchTypes']);
  if (fromSearchTypes != null) {
    common.setValueByPath(toObject, ['searchTypes'], fromSearchTypes);
  }

  const fromTimeRangeFilter = common.getValueByPath(fromObject, [
    'timeRangeFilter',
  ]);
  if (fromTimeRangeFilter != null) {
    common.setValueByPath(toObject, ['timeRangeFilter'], fromTimeRangeFilter);
  }

  return toObject;
}

export function imageConfigToMldev(
  fromObject: types.ImageConfig,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromAspectRatio = common.getValueByPath(fromObject, ['aspectRatio']);
  if (fromAspectRatio != null) {
    common.setValueByPath(toObject, ['aspectRatio'], fromAspectRatio);
  }

  const fromImageSize = common.getValueByPath(fromObject, ['imageSize']);
  if (fromImageSize != null) {
    common.setValueByPath(toObject, ['imageSize'], fromImageSize);
  }

  if (common.getValueByPath(fromObject, ['personGeneration']) !== undefined) {
    throw new Error(
      'personGeneration parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['outputMimeType']) !== undefined) {
    throw new Error(
      'outputMimeType parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['outputCompressionQuality']) !==
    undefined
  ) {
    throw new Error(
      'outputCompressionQuality parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['imageOutputOptions']) !== undefined) {
    throw new Error(
      'imageOutputOptions parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['prominentPeople']) !== undefined) {
    throw new Error(
      'prominentPeople parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function inlinedRequestToMldev(
  apiClient: ApiClient,
  fromObject: types.InlinedRequest,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModel = common.getValueByPath(fromObject, ['model']);
  if (fromModel != null) {
    common.setValueByPath(
      toObject,
      ['request', 'model'],
      t.tModel(apiClient, fromModel),
    );
  }

  const fromContents = common.getValueByPath(fromObject, ['contents']);
  if (fromContents != null) {
    let transformedList = t.tContents(fromContents);
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return contentToMldev(item);
      });
    }
    common.setValueByPath(toObject, ['request', 'contents'], transformedList);
  }

  const fromMetadata = common.getValueByPath(fromObject, ['metadata']);
  if (fromMetadata != null) {
    common.setValueByPath(toObject, ['metadata'], fromMetadata);
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    common.setValueByPath(
      toObject,
      ['request', 'generationConfig'],
      generateContentConfigToMldev(
        apiClient,
        fromConfig,
        common.getValueByPath(toObject, ['request'], {}) as Record<
          string,
          unknown
        >,
      ),
    );
  }

  return toObject;
}

export function inlinedResponseFromMldev(
  fromObject: types.InlinedResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromResponse = common.getValueByPath(fromObject, ['response']);
  if (fromResponse != null) {
    common.setValueByPath(
      toObject,
      ['response'],
      generateContentResponseFromMldev(
        fromResponse as types.GenerateContentResponse,
      ),
    );
  }

  const fromMetadata = common.getValueByPath(fromObject, ['metadata']);
  if (fromMetadata != null) {
    common.setValueByPath(toObject, ['metadata'], fromMetadata);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  return toObject;
}

export function listBatchJobsConfigToMldev(
  fromObject: types.ListBatchJobsConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromPageSize = common.getValueByPath(fromObject, ['pageSize']);
  if (parentObject !== undefined && fromPageSize != null) {
    common.setValueByPath(parentObject, ['_query', 'pageSize'], fromPageSize);
  }

  const fromPageToken = common.getValueByPath(fromObject, ['pageToken']);
  if (parentObject !== undefined && fromPageToken != null) {
    common.setValueByPath(parentObject, ['_query', 'pageToken'], fromPageToken);
  }

  if (common.getValueByPath(fromObject, ['filter']) !== undefined) {
    throw new Error(
      'filter parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function listBatchJobsConfigToVertex(
  fromObject: types.ListBatchJobsConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromPageSize = common.getValueByPath(fromObject, ['pageSize']);
  if (parentObject !== undefined && fromPageSize != null) {
    common.setValueByPath(parentObject, ['_query', 'pageSize'], fromPageSize);
  }

  const fromPageToken = common.getValueByPath(fromObject, ['pageToken']);
  if (parentObject !== undefined && fromPageToken != null) {
    common.setValueByPath(parentObject, ['_query', 'pageToken'], fromPageToken);
  }

  const fromFilter = common.getValueByPath(fromObject, ['filter']);
  if (parentObject !== undefined && fromFilter != null) {
    common.setValueByPath(parentObject, ['_query', 'filter'], fromFilter);
  }

  return toObject;
}

export function listBatchJobsParametersToMldev(
  fromObject: types.ListBatchJobsParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    listBatchJobsConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function listBatchJobsParametersToVertex(
  fromObject: types.ListBatchJobsParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    listBatchJobsConfigToVertex(fromConfig, toObject);
  }

  return toObject;
}

export function listBatchJobsResponseFromMldev(
  fromObject: types.ListBatchJobsResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromNextPageToken = common.getValueByPath(fromObject, [
    'nextPageToken',
  ]);
  if (fromNextPageToken != null) {
    common.setValueByPath(toObject, ['nextPageToken'], fromNextPageToken);
  }

  const fromBatchJobs = common.getValueByPath(fromObject, ['operations']);
  if (fromBatchJobs != null) {
    let transformedList = fromBatchJobs;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return batchJobFromMldev(item);
      });
    }
    common.setValueByPath(toObject, ['batchJobs'], transformedList);
  }

  return toObject;
}

export function listBatchJobsResponseFromVertex(
  fromObject: types.ListBatchJobsResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromNextPageToken = common.getValueByPath(fromObject, [
    'nextPageToken',
  ]);
  if (fromNextPageToken != null) {
    common.setValueByPath(toObject, ['nextPageToken'], fromNextPageToken);
  }

  const fromBatchJobs = common.getValueByPath(fromObject, [
    'batchPredictionJobs',
  ]);
  if (fromBatchJobs != null) {
    let transformedList = fromBatchJobs;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return batchJobFromVertex(item);
      });
    }
    common.setValueByPath(toObject, ['batchJobs'], transformedList);
  }

  return toObject;
}

export function partToMldev(fromObject: types.Part): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromMediaResolution = common.getValueByPath(fromObject, [
    'mediaResolution',
  ]);
  if (fromMediaResolution != null) {
    common.setValueByPath(toObject, ['mediaResolution'], fromMediaResolution);
  }

  const fromToolCall = common.getValueByPath(fromObject, ['toolCall']);
  if (fromToolCall != null) {
    common.setValueByPath(toObject, ['toolCall'], fromToolCall);
  }

  const fromToolResponse = common.getValueByPath(fromObject, ['toolResponse']);
  if (fromToolResponse != null) {
    common.setValueByPath(toObject, ['toolResponse'], fromToolResponse);
  }

  const fromAudioTranscription = common.getValueByPath(fromObject, [
    'audioTranscription',
  ]);
  if (fromAudioTranscription != null) {
    common.setValueByPath(
      toObject,
      ['audioTranscription'],
      fromAudioTranscription,
    );
  }

  const fromCodeExecutionResult = common.getValueByPath(fromObject, [
    'codeExecutionResult',
  ]);
  if (fromCodeExecutionResult != null) {
    common.setValueByPath(
      toObject,
      ['codeExecutionResult'],
      fromCodeExecutionResult,
    );
  }

  const fromExecutableCode = common.getValueByPath(fromObject, [
    'executableCode',
  ]);
  if (fromExecutableCode != null) {
    common.setValueByPath(toObject, ['executableCode'], fromExecutableCode);
  }

  const fromFileData = common.getValueByPath(fromObject, ['fileData']);
  if (fromFileData != null) {
    common.setValueByPath(
      toObject,
      ['fileData'],
      fileDataToMldev(fromFileData),
    );
  }

  const fromFunctionCall = common.getValueByPath(fromObject, ['functionCall']);
  if (fromFunctionCall != null) {
    common.setValueByPath(
      toObject,
      ['functionCall'],
      functionCallToMldev(fromFunctionCall),
    );
  }

  const fromFunctionResponse = common.getValueByPath(fromObject, [
    'functionResponse',
  ]);
  if (fromFunctionResponse != null) {
    common.setValueByPath(toObject, ['functionResponse'], fromFunctionResponse);
  }

  const fromInlineData = common.getValueByPath(fromObject, ['inlineData']);
  if (fromInlineData != null) {
    common.setValueByPath(
      toObject,
      ['inlineData'],
      blobToMldev(fromInlineData),
    );
  }

  const fromText = common.getValueByPath(fromObject, ['text']);
  if (fromText != null) {
    common.setValueByPath(toObject, ['text'], fromText);
  }

  const fromThought = common.getValueByPath(fromObject, ['thought']);
  if (fromThought != null) {
    common.setValueByPath(toObject, ['thought'], fromThought);
  }

  const fromThoughtSignature = common.getValueByPath(fromObject, [
    'thoughtSignature',
  ]);
  if (fromThoughtSignature != null) {
    common.setValueByPath(toObject, ['thoughtSignature'], fromThoughtSignature);
  }

  const fromVideoMetadata = common.getValueByPath(fromObject, [
    'videoMetadata',
  ]);
  if (fromVideoMetadata != null) {
    common.setValueByPath(toObject, ['videoMetadata'], fromVideoMetadata);
  }

  const fromPartMetadata = common.getValueByPath(fromObject, ['partMetadata']);
  if (fromPartMetadata != null) {
    common.setValueByPath(toObject, ['partMetadata'], fromPartMetadata);
  }

  return toObject;
}

export function safetySettingToMldev(
  fromObject: types.SafetySetting,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromCategory = common.getValueByPath(fromObject, ['category']);
  if (fromCategory != null) {
    common.setValueByPath(toObject, ['category'], fromCategory);
  }

  if (common.getValueByPath(fromObject, ['method']) !== undefined) {
    throw new Error(
      'method parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromThreshold = common.getValueByPath(fromObject, ['threshold']);
  if (fromThreshold != null) {
    common.setValueByPath(toObject, ['threshold'], fromThreshold);
  }

  return toObject;
}

export function toolConfigToMldev(
  fromObject: types.ToolConfig,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFunctionCallingConfig = common.getValueByPath(fromObject, [
    'functionCallingConfig',
  ]);
  if (fromFunctionCallingConfig != null) {
    common.setValueByPath(
      toObject,
      ['functionCallingConfig'],
      functionCallingConfigToMldev(fromFunctionCallingConfig),
    );
  }

  const fromRetrievalConfig = common.getValueByPath(fromObject, [
    'retrievalConfig',
  ]);
  if (fromRetrievalConfig != null) {
    common.setValueByPath(toObject, ['retrievalConfig'], fromRetrievalConfig);
  }

  const fromIncludeServerSideToolInvocations = common.getValueByPath(
    fromObject,
    ['includeServerSideToolInvocations'],
  );
  if (fromIncludeServerSideToolInvocations != null) {
    common.setValueByPath(
      toObject,
      ['includeServerSideToolInvocations'],
      fromIncludeServerSideToolInvocations,
    );
  }

  return toObject;
}

export function toolToMldev(fromObject: types.Tool): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['retrieval']) !== undefined) {
    throw new Error(
      'retrieval parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromGoogleMaps = common.getValueByPath(fromObject, ['googleMaps']);
  if (fromGoogleMaps != null) {
    common.setValueByPath(
      toObject,
      ['googleMaps'],
      googleMapsToMldev(fromGoogleMaps),
    );
  }

  const fromMcpServers = common.getValueByPath(fromObject, ['mcpServers']);
  if (fromMcpServers != null) {
    let transformedList = fromMcpServers;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['mcpServers'], transformedList);
  }

  const fromCodeExecution = common.getValueByPath(fromObject, [
    'codeExecution',
  ]);
  if (fromCodeExecution != null) {
    common.setValueByPath(toObject, ['codeExecution'], fromCodeExecution);
  }

  const fromComputerUse = common.getValueByPath(fromObject, ['computerUse']);
  if (fromComputerUse != null) {
    common.setValueByPath(toObject, ['computerUse'], fromComputerUse);
  }

  if (
    common.getValueByPath(fromObject, ['enterpriseWebSearch']) !== undefined
  ) {
    throw new Error(
      'enterpriseWebSearch parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['exaAiSearch']) !== undefined) {
    throw new Error(
      'exaAiSearch parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromFunctionDeclarations = common.getValueByPath(fromObject, [
    'functionDeclarations',
  ]);
  if (fromFunctionDeclarations != null) {
    let transformedList = fromFunctionDeclarations;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['functionDeclarations'], transformedList);
  }

  const fromGoogleSearch = common.getValueByPath(fromObject, ['googleSearch']);
  if (fromGoogleSearch != null) {
    common.setValueByPath(
      toObject,
      ['googleSearch'],
      googleSearchToMldev(fromGoogleSearch),
    );
  }

  const fromGoogleSearchRetrieval = common.getValueByPath(fromObject, [
    'googleSearchRetrieval',
  ]);
  if (fromGoogleSearchRetrieval != null) {
    common.setValueByPath(
      toObject,
      ['googleSearchRetrieval'],
      fromGoogleSearchRetrieval,
    );
  }

  if (common.getValueByPath(fromObject, ['parallelAiSearch']) !== undefined) {
    throw new Error(
      'parallelAiSearch parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromUrlContext = common.getValueByPath(fromObject, ['urlContext']);
  if (fromUrlContext != null) {
    common.setValueByPath(toObject, ['urlContext'], fromUrlContext);
  }

  const fromFileSearch = common.getValueByPath(fromObject, ['fileSearch']);
  if (fromFileSearch != null) {
    common.setValueByPath(toObject, ['fileSearch'], fromFileSearch);
  }

  return toObject;
}

export function vertexMultimodalDatasetDestinationFromVertex(
  fromObject: types.VertexMultimodalDatasetDestination,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromBigqueryDestination = common.getValueByPath(fromObject, [
    'bigqueryDestination',
    'outputUri',
  ]);
  if (fromBigqueryDestination != null) {
    common.setValueByPath(
      toObject,
      ['bigqueryDestination'],
      fromBigqueryDestination,
    );
  }

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (fromDisplayName != null) {
    common.setValueByPath(toObject, ['displayName'], fromDisplayName);
  }

  return toObject;
}

export function vertexMultimodalDatasetDestinationToVertex(
  fromObject: types.VertexMultimodalDatasetDestination,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromBigqueryDestination = common.getValueByPath(fromObject, [
    'bigqueryDestination',
  ]);
  if (fromBigqueryDestination != null) {
    common.setValueByPath(
      toObject,
      ['bigqueryDestination', 'outputUri'],
      fromBigqueryDestination,
    );
  }

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (fromDisplayName != null) {
    common.setValueByPath(toObject, ['displayName'], fromDisplayName);
  }

  return toObject;
}
